<template>
  <div class="h-full">
    <TransitionRoot as="template" :show="isSidebarOpen">
      <Dialog as="div" class="relative z-40" @close="isSidebarOpen = false">
        <TransitionChild
          as="template"
          enter="transition-opacity ease-linear duration-300"
          enter-from="opacity-0"
          enter-to="opacity-100"
          leave="transition-opacity ease-linear duration-300"
          leave-from="opacity-100"
          leave-to="opacity-0"
        >
          <div class="fixed inset-0 bg-primary-900/80" />
        </TransitionChild>

        <div class="fixed inset-0 flex">
          <TransitionChild
            as="template"
            enter="transition ease-in-out duration-300 transform"
            enter-from="-translate-x-full"
            enter-to="translate-x-0"
            leave="transition ease-in-out duration-300 transform"
            leave-from="translate-x-0"
            leave-to="-translate-x-full"
          >
            <DialogPanel class="relative mr-16 flex w-full max-w-xs flex-1">
              <TransitionChild
                as="template"
                enter="ease-in-out duration-300"
                enter-from="opacity-0"
                enter-to="opacity-100"
                leave="ease-in-out duration-300"
                leave-from="opacity-100"
                leave-to="opacity-0"
              >
                <div class="absolute left-full top-0 flex w-16 justify-center pt-5">
                  <button type="button" class="-m-2.5 p-2.5" @click="isSidebarOpen = false">
                    <span class="sr-only">Close sidebar</span>
                    <Close class="h-6 w-6 text-white" aria-hidden="true" />
                  </button>
                </div>
              </TransitionChild>

              <div class="flex grow flex-col gap-y-5 overflow-y-auto bg-primary-900 px-6 pb-2 ring-1 ring-white/10">
                <router-link to="/" class="flex h-16 shrink-0 items-center" active-class="pointer-events-none">
                  <CompanyLogo class="h-8 w-auto text-white" />
                </router-link>
                <nav class="flex flex-1 flex-col">
                  <ul role="list" class="-mx-2 flex-1 space-y-1">
                    <li v-for="item in navigation" :key="item.name">
                      <router-link
                        :to="item.to"
                        class="group flex gap-x-3 rounded-md p-2 text-sm leading-6 font-semibold text-white/80 hover:text-white hover:bg-primary-800"
                        active-class="bg-primary-800 text-white pointer-events-none"
                      >
                        <component :is="item.icon" class="h-6 w-6 shrink-0" aria-hidden="true" />
                        {{ item.name }}
                      </router-link>
                    </li>
                  </ul>
                </nav>
              </div>
            </DialogPanel>
          </TransitionChild>
        </div>
      </Dialog>
    </TransitionRoot>

    <!-- Static sidebar for desktop -->
    <div class="hidden lg:fixed lg:inset-y-0 lg:left-0 lg:z-40 lg:block lg:overflow-y-auto lg:bg-primary-900 transition-[width]" :class="isSidebarWide ? 'lg:w-sidebar' : 'lg:w-14'">
      <div class="flex flex-col h-full">
        <router-link to="/" class="h-16 w-14 flex items-center justify-center shrink-0" active-class="pointer-events-none">
          <CompanyLogo class="h-5 w-auto text-white" />
        </router-link>
        <nav class="mt-8 grow">
          <ul role="list" class="flex flex-col space-y-1">
            <li v-for="item in navigation" :key="item.name" class="px-1">
              <router-link
                :to="item.to"
                class="group flex gap-x-3 rounded-md p-3 text-sm leading-6 font-semibold text-white/80 hover:text-white hover:bg-primary-800"
                active-class="bg-primary-800 text-white pointer-events-none"
              >
                <component :is="item.icon" class="h-6 w-6 shrink-0" aria-hidden="true" />
                <span :class="{ 'sr-only': !isSidebarWide }">{{ item.name }}</span>
              </router-link>
            </li>
          </ul>
        </nav>
        <div class="flex shrink-0 items-center justify-end">
          <button type="button" class="w-14 p-2.5 text-white/80 hover:text-white" @click="isSidebarWide = !isSidebarWide">
            <ChevronRight class="h-6 w-6 inline-block transition-transform" aria-hidden="true" :class="{ 'rotate-180': isSidebarWide }" />
          </button>
        </div>
      </div>
    </div>

    <div class="h-full transition-[padding]" :class="isSidebarWide ? 'lg:pl-sidebar' : 'lg:pl-14'">
      <div class="h-full flex flex-col">
        <div class="main-container sticky top-0 z-20 flex h-16 shrink-0 items-center gap-x-4 border-b border-primary-300 bg-white shadow-sm sm:gap-x-6">
          <button type="button" class="-m-2.5 p-2.5 text-gray-700 lg:hidden" @click="isSidebarOpen = true">
            <span class="sr-only">Open sidebar</span>
            <Bars3 class="h-6 w-6" aria-hidden="true" />
          </button>

          <slot name="navbar" />

          <!-- Separator -->
          <div class="h-6 w-px bg-primary-900/10 lg:hidden" aria-hidden="true" />

          <div class="flex flex-1 gap-x-4 self-stretch lg:gap-x-6">
            <div class="relative flex flex-1" />
            <div class="flex items-center gap-x-4 lg:gap-x-6">
              <button type="button" class="-m-2.5 p-2.5 text-gray-600 hover:text-gray-700">
                <span class="sr-only">View notifications</span>
                <BellOutline class="h-6 w-6" aria-hidden="true" />
              </button>

              <!-- Separator -->
              <div class="hidden lg:block lg:h-6 lg:w-px lg:bg-primary-900/10" aria-hidden="true" />

              <!-- Profile dropdown -->
              <Menu as="div" class="relative">
                <MenuButton class="-m-1.5 flex items-center p-1.5 text-gray-600 hover:text-gray-700">
                  <span class="sr-only">Open user menu</span>
                  <span class="hidden lg:flex lg:items-center">
                    <span class="text-sm font-semibold leading-6" aria-hidden="true">{{ sessionInfo.name }}</span>
                    <ChevronDown class="mx-2 h-5 w-5 text-gray-400" aria-hidden="true" />
                  </span>
                  <user-avatar />
                </MenuButton>
                <transition
                  enter-active-class="transition ease-out duration-100"
                  enter-from-class="transform opacity-0 scale-95"
                  enter-to-class="transform opacity-100 scale-100"
                  leave-active-class="transition ease-in duration-75"
                  leave-from-class="transform opacity-100 scale-100"
                  leave-to-class="transform opacity-0 scale-95"
                >
                  <MenuItems class="absolute right-0 z-10 mt-2.5 w-32 origin-top-right rounded-md bg-white py-2 shadow-lg ring-1 ring-gray-900/5 focus:outline-none">
                    <MenuItem v-for="item in userNavigation" :key="item.name" v-slot="{ active }">
                      <router-link
                        :to="item.to"
                        class="block px-3 py-1 text-sm leading-6 text-gray-900"
                        :class="{ 'bg-primary-50': active }"
                      >
                        {{ item.name }}
                      </router-link>
                    </MenuItem>
                  </MenuItems>
                </transition>
              </Menu>
            </div>
          </div>
        </div>

        <main class="grow bg-gray-50 overflow-hidden">
          <slot />
        </main>
      </div>
    </div>
  </div>
</template>

<script setup>

// NOTE:
// - breakpoint for mobile is 'lg'
// - sidebar width for expanded view is defined in tailwind.config.js with 'sidebar' key

import { shallowRef } from 'vue';
import { sessionInfo } from '../composables/useAuth';

import {
  Dialog,
  DialogPanel,
  Menu,
  MenuButton,
  MenuItem,
  MenuItems,
  TransitionChild,
  TransitionRoot
} from '@headlessui/vue';

import Bars3 from 'vue-material-design-icons/Menu.vue';
import BellOutline from 'vue-material-design-icons/BellOutline.vue';
import Calendar from 'vue-material-design-icons/Calendar.vue';
import Close from 'vue-material-design-icons/Close.vue';
import ChevronDown from 'vue-material-design-icons/ChevronDown.vue';
import ChevronRight from 'vue-material-design-icons/ChevronRight.vue';
import Dashboard from 'vue-material-design-icons/ViewDashboard.vue';

import UserAvatar from '../components/UserAvatar.vue';
import CompanyLogo from '../components/CompanyLogo.vue';

const navigation = [
  { name: 'Dashboard', to: '/dashboard/', icon: Calendar, current: false },
  { name: 'Grid', to: '/grid/', icon: Dashboard, current: false }
];
const userNavigation = [
  { name: 'Your profile', to: '/FIXME/' },
  { name: 'Sign out', to: '/logout/' }
];

const isSidebarOpen = shallowRef(false);
const isSidebarWide = shallowRef(false);
</script>
